import React, { useState } from "react";
import { Box, Avatar, IconButton, Typography, Tooltip } from "@mui/material";
import { DeleteIcon, PhotoCameraIcon } from "../../../icons";
import { observer } from "mobx-react-lite";
import { useRootStore } from "../../../store/contexts";
import { AvatarUploadDialog } from "../../../common/components/AvatarUploadDialog";
import { useAuthenticatedImage } from "../../../common/hooks/useAuthenticatedImage";
import type { FileData } from "../../../api/graphql";

interface AvatarFieldProps {
  avatarData?: FileData | null;
  entityName?: string;
  fieldName?: string;
  entityId?: string;
  displayName?: string;
  label?: string;
  previewUrl?: string | null;
  onAvatarChange: (blob: Blob) => void;
  onAvatarDelete?: () => void;
  disabled?: boolean;
  size?: number;
}

export const AvatarField = observer(({
  avatarData,
  entityName = "",
  fieldName = "",
  entityId = "",
  displayName = "",
  label,
  previewUrl,
  onAvatarChange,
  onAvatarDelete,
  disabled = false,
  size = 100
}: AvatarFieldProps) => {
  const { imageUrl: serverImageUrl } = useAuthenticatedImage(
    avatarData || null,
    entityName,
    fieldName,
    entityId
  );
  // Priorizar previewUrl (imagen recién seleccionada) sobre la del servidor
  const imageUrl = previewUrl || serverImageUrl;
  const { langStore } = useRootStore();
  const [dialogOpen, setDialogOpen] = useState(false);

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleSave = (blob: Blob) => {
    onAvatarChange(blob);
    setDialogOpen(false);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1 }}>
      {label && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {label}
        </Typography>
      )}

      <Box sx={{ position: "relative", display: "inline-block" }}>
        <Avatar
          src={imageUrl || undefined}
          sx={{
            width: size,
            height: size,
            fontSize: size / 2.5,
            bgcolor: imageUrl ? "transparent" : "primary.main"
          }}
        >
          {!imageUrl && getInitials(displayName)}
        </Avatar>

        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
            display: "flex",
            gap: 0.5
          }}
        >
          <Tooltip title={langStore.changeAvatarLabel || "Change avatar"}>
            <IconButton
              size="small"
              onClick={() => setDialogOpen(true)}
              disabled={disabled}
              sx={{
                bgcolor: "primary.main",
                color: "common.white",
                "&:hover": {
                  bgcolor: "primary.dark"
                },
                width: 28,
                height: 28
              }}
            >
              <PhotoCameraIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>

          {(imageUrl || avatarData) && onAvatarDelete && (
            <Tooltip title={langStore.deleteCommand || "Delete"}>
              <IconButton
                size="small"
                onClick={onAvatarDelete}
                disabled={disabled}
                sx={{
                  bgcolor: "error.main",
                  color: "common.white",
                  "&:hover": {
                    bgcolor: "error.dark"
                  },
                  width: 28,
                  height: 28
                }}
              >
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <AvatarUploadDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
      />
    </Box>
  );
});
